/**
 * 見積書（PC）の「⋯」（#173 までは「≡」）から開く窓（#119、docs/56 §6.1）：
 *   ・見積依頼書を作成…＝行に付いた外注先を選ぶ → その外注先の依頼を開く（無ければ下書きを作る）
 *     外注先の付いた行が無いときは、窓の中で外注先と行を選ぶ（選んだ行に外注先が付く＝行の「見積依頼に入れる…」と同じ。10/7 射場さん）
 *   ・写真台帳（PDF）…＝3枚／6枚・冊（60枚ずつ）を選んで開く（field の会社だけ。保存しない）
 */
import { ESTIMATE_PC_MSG, MSG, RFQ_MSG, pickNamesOf, type EstimateRfqs, type LinePhotoList, type RfqList } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api.js";
import { ErrorText } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { usePdfTab } from "../../lib/pdfOpen.js";
import { RfqStatus } from "./parts.jsx";

/** 窓で選べる行（明細・文章の行。見積の画面の並び） */
export type RfqPickLine = { id: string; name: string; spec: string };

export function RfqVendorDialog({ data, pick, onPick, onClose }: {
  data: EstimateRfqs;
  /** 外注先の付いた行が無いときに、窓の中で外注先と行を選ぶ（書ける見積だけ。出した見積は行を変えられないので渡さない） */
  pick?: { vendors: Array<{ id: string; full: string; label: string }>; lines: RfqPickLine[]; customer: string; onAssign: (vendorId: string, lineIds: string[]) => void } | undefined;
  onPick: (vendorId: string) => void;
  onClose: () => void;
}) {
  const rows = data.vendors.filter((v) => v.lineCount > 0 || v.current);
  if (!rows.length && pick) return <RfqPickDialog pick={pick} onClose={onClose} />;
  // 外注先は略称（#149）。同じ略称が2社あるときだけ「略称（正式名）」
  const names = pickNamesOf(rows.map((v) => ({ short: v.vendor.shortName, full: v.vendor.name })));
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg" role="dialog" aria-label={t(RFQ_MSG.pickVendor)} onKeyDown={(e) => e.key === "Escape" && onClose()} data-testid="rfq-vendor-dialog">
        <div className="dh">{t(RFQ_MSG.pickVendor)}</div>
        <div className="db">
          {rows.length ? (
            <ul className="rqvlist">
              {rows.map((v, i) => (
                <li key={v.vendor.id}>
                  <button type="button" className="btn" autoFocus={i === 0} onClick={() => onPick(v.vendor.id)} data-testid="rfq-vendor-pick">
                    <span className="rqvn" title={v.vendor.name}>{names[i]}</span>
                    <span className="muted">{`${v.lineCount}行`}</span>
                    {v.current ? <RfqStatus s={v.current} compact /> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0 }} data-testid="rfq-no-vendor">{t(RFQ_MSG.noVendorLines)}</p>
          )}
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onClose}>取消</button>
        </div>
      </div>
    </div>
  );
}

/** 外注先と行を選ぶ窓：決めると選んだ行に外注先を付けて保存し、その外注先の見積依頼書（下書き）を開く */
function RfqPickDialog({ pick, onClose }: { pick: NonNullable<Parameters<typeof RfqVendorDialog>[0]["pick"]>; onClose: () => void }) {
  const [vendor, setVendor] = useState(pick.vendors[0]?.id ?? "");
  const [sel, setSel] = useState<Set<string>>(() => new Set());
  const all = pick.lines.length > 0 && sel.size === pick.lines.length;
  const toggle = (id: string, on: boolean) =>
    setSel((x) => {
      const n = new Set(x);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="dlg rqpick"
        role="dialog"
        aria-label={RFQ_MSG.create}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        data-testid="rfq-pick-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (vendor && sel.size) pick.onAssign(vendor, pick.lines.filter((l) => sel.has(l.id)).map((l) => l.id));
        }}
      >
        <div className="dh">{RFQ_MSG.create}</div>
        <div className="db">
          {!pick.vendors.length ? (
            <ErrorText>{ESTIMATE_PC_MSG.requestNoVendor.replace("{customer}", pick.customer)}</ErrorText>
          ) : (
            <>
              <div className="field">
                <label htmlFor="rq_pick_vendor">{t("{vendor}")}</label>
                <select id="rq_pick_vendor" className="inp" value={vendor} autoFocus onChange={(e) => setVendor(e.target.value)} data-testid="rfq-pick-vendor">
                  {pick.vendors.map((x) => (
                    <option key={x.id} value={x.id}>{x.label}</option>
                  ))}
                </select>
              </div>
              {pick.lines.length ? (
                <fieldset className="rqpicklines">
                  <legend>{RFQ_MSG.pickLines}</legend>
                  <button type="button" className="btn text sm" onClick={() => setSel(all ? new Set() : new Set(pick.lines.map((l) => l.id)))} data-testid="rfq-pick-all">
                    {all ? RFQ_MSG.pickNone : RFQ_MSG.pickAll}
                  </button>
                  <ul>
                    {pick.lines.map((l) => (
                      <li key={l.id}>
                        <label className="chk">
                          <input type="checkbox" checked={sel.has(l.id)} onChange={(e) => toggle(l.id, e.target.checked)} data-testid="rfq-pick-line" />
                          <span>
                            {l.name || t("（{lineName}なし）")}
                            {l.spec ? <span className="muted">{`　${l.spec}`}</span> : null}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </fieldset>
              ) : (
                <p className="hint" style={{ margin: 0 }} data-testid="rfq-pick-nolines">{RFQ_MSG.pickNoLines}</p>
              )}
            </>
          )}
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={!vendor || !sel.size} data-testid="rfq-pick-apply">{RFQ_MSG.create}</button>
        </div>
      </form>
    </div>
  );
}

export function PhotoLedgerDialog({ estimateId, onClose }: { estimateId: string; onClose: () => void }) {
  const [per, setPer] = useState<3 | 6>(3);
  const [book, setBook] = useState(1);
  const q = useQuery({ queryKey: ["estimate-photos", estimateId], queryFn: () => api<LinePhotoList>("GET", `/estimates/${estimateId}/photos`) });
  const inLines = q.data ? Math.max(0, q.data.total - q.data.unassignedCount) : 0;
  const books = Math.max(1, Math.ceil(inLines / 60));
  const url = `/api/v1/estimates/${estimateId}/photo-ledger?perPage=${per}&book=${book}`;
  // 押した瞬間に新しいタブで「PDF 作成中…」。作成中は窓を開いたまま押せない（docs/72 §10）、できたら閉じる。写真台帳は保存しない＝取った中身をそのまま見せる
  const tab = usePdfTab();
  const making = tab.busy != null;
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg" role="dialog" aria-label={RFQ_MSG.ledgerTitle} onKeyDown={(e) => e.key === "Escape" && onClose()} data-testid="photo-ledger-dialog">
        <div className="dh">{RFQ_MSG.ledgerTitle}</div>
        <div className="db">
          <div className="seg" role="group" aria-label={RFQ_MSG.ledgerTitle}>
            {([3, 6] as const).map((n) => (
              <button key={n} type="button" aria-pressed={per === n} onClick={() => setPer(n)} data-testid={`ledger-per-${n}`}>
                {n === 3 ? RFQ_MSG.perPage3 : RFQ_MSG.perPage6}
              </button>
            ))}
          </div>
          {books > 1 ? (
            <div className="seg" role="group" aria-label="冊" style={{ marginTop: 12 }}>
              {Array.from({ length: books }, (_, i) => i + 1).map((b) => (
                <button key={b} type="button" aria-pressed={book === b} onClick={() => setBook(b)} data-testid={`ledger-book-${b}`}>
                  {RFQ_MSG.ledgerBook.replace("{n}", String(b)).replace("{a}", String((b - 1) * 60 + 1)).replace("{b}", String(Math.min(b * 60, inLines)))}
                </button>
              ))}
            </div>
          ) : null}
          {q.data && inLines === 0 ? <p className="hint" data-testid="ledger-empty">写真がありません</p> : null}
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onClose}>取消</button>
          {inLines > 0 ? (
            <button type="button" className="btn primary" disabled={making} aria-busy={making} onClick={() => void tab.open(url).then((ok) => ok && onClose())} data-testid="ledger-open">
              {making ? MSG.pdfMaking : RFQ_MSG.pdfOpen}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * 見積の行の「…」→「見積依頼に追加…」（#218、docs/88 §4.5）：{vendor}と依頼（その{vendor}の下書き／新しい依頼）を選ぶ → その行を写す。
 * 行の外注先が空ならその{vendor}を入れる（画面が入れてから保存する）。同じ行を何社にでも出せる
 */
export function RfqAddLineDialog({ estimateId, groupId, lineName, value, vendors, customer, onApply, onClose }: {
  estimateId: string;
  groupId: string | null;
  lineName: string;
  /** 行の外注先（既定に選ぶ） */
  value: string | null;
  vendors: Array<{ id: string; name: string; full: string; label: string }>;
  customer: string;
  onApply: (vendorId: string, rfqId: string | null) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState<string>(value ?? vendors[0]?.id ?? "");
  const [target, setTarget] = useState<string>("");
  const drafts = useQuery({ queryKey: ["rfqs", "group", groupId, "draft"], queryFn: () => api<RfqList>("GET", `/rfqs?f=draft&g=${groupId}`), enabled: !!groupId });
  const mine = (drafts.data?.items ?? []).filter((x) => x.vendor.id === v && (!x.estimate || x.estimate.id === estimateId));
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="dlg"
        role="dialog"
        aria-label={RFQ_MSG.addLineTitle}
        data-testid="request-dialog"
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        onSubmit={(e) => {
          e.preventDefault();
          if (v) onApply(v, target || null);
        }}
      >
        <div className="dh">{RFQ_MSG.addLineTitle}</div>
        <div className="db">
          <p style={{ marginTop: 0 }}>
            <b>{lineName || t("（{lineName}なし）")}</b>
          </p>
          {vendors.length ? (
            <>
              <label className="vql-fld">
                <span>{t("{vendor}")}</span>
                <select value={v} autoFocus onChange={(e) => { setV(e.target.value); setTarget(""); }} data-testid="request-vendor">
                  {vendors.map((x) => (
                    <option key={x.id} value={x.id}>{x.label}</option>
                  ))}
                </select>
              </label>
              <label className="vql-fld" style={{ marginTop: 12 }}>
                <span>{RFQ_MSG.addLineTarget}</span>
                <select value={target} disabled={!drafts.data} onChange={(e) => setTarget(e.target.value)} data-testid="request-target">
                  <option value="">{RFQ_MSG.addLineNew}</option>
                  {mine.map((x) => (
                    <option key={x.id} value={x.id}>{`${RFQ_MSG.draftNo}　${x.subject}（${x.itemCount}行）`}</option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <ErrorText>{ESTIMATE_PC_MSG.requestNoVendor.replace("{customer}", customer)}</ErrorText>
          )}
        </div>
        <div className="df">
          <button type="button" className="btn quiet" onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={!v} data-testid="request-apply">{RFQ_MSG.addLineDo}</button>
        </div>
      </form>
    </div>
  );
}

